// apps/mcv-client/src/components/Layout/SideNav/VaultSideMenu.tsx
import { Show, SignOutButton } from '@clerk/react-router';
import { ArrowLeftStartOnRectangleIcon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { UserContextV2 } from '~/app';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { useLoadedProvider } from '~/features/vault/account/vaultSelectors';
import { paths } from '~/routing';
import { DownloadMyDataLink } from './DownloadMyDataLink';
import { NavComponent } from './NavComponent';
import { ProfileSection } from './ProviderSection';
import { VaultHlxSwitchLink } from './VaultHlxSwitchLink';
import { getSidebarProviderAccountPagesLinks } from './constants';
import type { SideMenuProps } from './types';

export const VaultSideMenu = ({
  keyPrefix,
  onCollapse,
  switchTargetPath,
}: SideMenuProps & {
  /** Role-aware destination on HLX when cross-brand navigation is available. */
  switchTargetPath?: string;
}) => {
  const { provider, vaultRef } = useLoadedProvider();
  const isDelegating = UserContextV2.useSelector((state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const sections = getSidebarProviderAccountPagesLinks({
    includeProviderSettings: !isDelegating,
  });

  if (!provider || !vaultRef) {
    return <LoadingSpinner />;
  }

  return (
    <div className="relative bg-primary-t-900 rounded-lg w-72 flex flex-col h-full min-h-[25vh]">
      {onCollapse && (
        <button
          type="button"
          onClick={onCollapse}
          aria-label="Collapse sidebar"
          title="Collapse sidebar"
          className="absolute top-2 right-2 z-10 p-1.5 rounded-md text-grey-400 hover:text-grey-200 hover:bg-white/10 transition-colors hover:cursor-pointer"
        >
          <ChevronDoubleLeftIcon className="size-5" />
        </button>
      )}
      <ProfileSection provider={provider} />
      {!isDelegating && (
        <div className="relative border-b border-primary-t-700 py-2">
          <VaultHlxSwitchLink switchTargetPath={switchTargetPath} />
        </div>
      )}
      <div className="flex-1 overflow-y-auto py-1">
        <NavComponent keyPrefix={keyPrefix} actorRef={vaultRef} sections={sections} />
      </div>
      <div className="border-t border-primary-t-700 px-2 py-2 flex flex-col gap-0.5">
        <DownloadMyDataLink providerId={provider.id} />
        {/* [MCV-1752] While impersonating, Sign Out would end the employer's own session; they exit through the banner instead. */}
        {!isDelegating && (
          <Show when="signed-in">
            <SignOutButton data-cy={CT.Homepage.signoutBtn} redirectUrl={paths.landing}>
              <div className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-grey-200 hover:bg-white/50 transition-colors duration-150">
                <ArrowLeftStartOnRectangleIcon className="size-4 text-grey-400" />
                <span className="text-sm" data-cy="sign-out-button">
                  Sign Out
                </span>
              </div>
            </SignOutButton>
          </Show>
        )}
      </div>
    </div>
  );
};
